import { datadogRum } from '@datadog/browser-rum';

/*
  Single quarantine point for every dependency on the third-party 360° spinner MFE's
  internals: its window globals, its React/Redux store (reached via the fiber tree), and
  the DOM chrome it renders. The rest of the feature depends on these typed accessors, not
  on the fragile shapes below — so an MFE version bump breaks (and is fixed) in ONE file
  with a compile-time surface, instead of silently across the feature.

  Everything here is best-effort: the MFE is separately deployed and its internals are
  undocumented, so every accessor feature-detects and returns null / no-ops rather than
  throwing when the shape it expects isn't found.
*/

interface SpinnerSpin {
  style?: string;
  images?: { urls?: string[] };
  cameras?: { frameCount?: number };
}

interface SpinnerNode {
  data?: { spins?: SpinnerSpin[] };
}

interface SpinnerReduxStore {
  getState: () => { spinner?: { ui?: { exterior?: { angle?: number } } } } | undefined;
}

type SpinnerWindow = typeof window & {
  currentSpinnerNode?: SpinnerNode;
  spinToAngle?: (deg: number) => void;
};

const spinnerWindow = (): SpinnerWindow => window as SpinnerWindow;

const SPINNER_CONTAINER_SELECTOR = '[data-qa="spinner-app-container"]';

// Silent-degradation alerting. These accessors read undocumented MFE internals; a new
// spinner version can change a shape so a read returns null WITHOUT any error — which would
// quietly worsen detection. When the spinner IS mounted but an expected internal is absent,
// emit a one-per-signal-per-session RUM action so a version drift pages us instead of
// hiding. Deduped so a per-frame read can't flood.
const reportedMisses = new Set<string>();
// Exported for the feature's other MFE-shape dependents (e.g. useDamagePins' hotspot-overflow
// read) so their silent degradations surface in Datadog through the same deduped channel.
export function reportShapeMiss(signal: string): void {
  if (reportedMisses.has(signal)) return;
  reportedMisses.add(signal);
  try {
    datadogRum.addAction('sar-spinner-mfe-shape-miss', { signal });
  } catch {
    /* never let telemetry break a read */
  }
}
// Reset per spinner session so a genuinely new session re-reports (called on mount).
export function resetSpinnerShapeMissReports(): void {
  reportedMisses.clear();
}
function spinnerMounted(): boolean {
  return !!document.querySelector(SPINNER_CONTAINER_SELECTOR);
}

// Cache the resolved store per root element. A WeakMap lets a detached spinner root (after
// unmount/remount) be garbage-collected instead of being pinned by a module-level singleton.
const storeCache = new WeakMap<Element, SpinnerReduxStore>();

// The MFE exposes no store API, so walk up its fiber tree to the provider that carries the
// Redux store. Depth-bounded so a structural change can't spin.
function getSpinnerStore(): SpinnerReduxStore | null {
  const rootEl = document.querySelector(SPINNER_CONTAINER_SELECTOR);
  if (!rootEl) return null;
  const cached = storeCache.get(rootEl);
  if (cached) return cached;

  const fiberKey = Object.keys(rootEl).find(
    (k) => k.startsWith('__reactFiber$') || k.startsWith('__reactContainer$')
  );
  if (!fiberKey) {
    reportShapeMiss('store-fiber-key-missing');
    return null;
  }

  let fiber = (rootEl as unknown as Record<string, { memoizedProps?: { store?: unknown }; return?: unknown }>)[fiberKey];
  for (let depth = 0; fiber && depth < 200; depth++, fiber = fiber.return as typeof fiber) {
    const store = fiber.memoizedProps?.store as SpinnerReduxStore | undefined;
    if (store && typeof store.getState === 'function') {
      storeCache.set(rootEl, store);
      return store;
    }
  }
  reportShapeMiss('store-not-found');
  return null;
}

// Turntable angle, normalized to [0, 360). 0° = front, 90° = driver/left, 180° = rear,
// 270° = passenger/right. null when the spinner isn't ready / the shape isn't found.
export function getSpinnerAngleDeg(): number | null {
  try {
    const store = getSpinnerStore();
    if (!store) return null; // store-shape misses already reported by getSpinnerStore
    const angle = store.getState()?.spinner?.ui?.exterior?.angle;
    if (typeof angle !== 'number' || Number.isNaN(angle)) {
      // Store resolved but the angle path is gone → a shape change, not just "not ready".
      reportShapeMiss('angle-path-missing');
      return null;
    }
    return ((angle % 360) + 360) % 360;
  } catch {
    return null;
  }
}

// The first spin descriptor (frame URLs, camera frame count, imaging style).
export function getFirstSpin(): SpinnerSpin | null {
  try {
    const spin = spinnerWindow().currentSpinnerNode?.data?.spins?.[0] ?? null;
    // Global renamed/reshaped while the spinner is mounted → capture loses frame data.
    if (!spin && spinnerMounted()) reportShapeMiss('spin-node-missing');
    return spin;
  } catch {
    return null;
  }
}

// Ask the MFE to tween to an absolute turntable angle (no-op if unavailable).
export function spinToAngle(deg: number): void {
  try {
    const fn = spinnerWindow().spinToAngle;
    if (typeof fn === 'function') fn(deg);
  } catch {
    /* MFE not ready */
  }
}

// Hide the MFE's own chrome so it can't be interacted with over our marking UI:
//   - the immersive-exit "X" (tapping it kills the spin view with no recovery),
//   - the Exterior/Interior view toggle,
//   - the "rotate to view" drag hint / rotation scrubber,
//   - the "360°" drag-hint badge.
// The MFE re-renders this chrome asynchronously, so callers re-run this (see
// useHideSpinnerChrome). Elements containing the car <canvas> are never hidden or climbed
// into, so the spinner itself can't be hidden by a structural change.
// SERV-583 (bug 3): the MFE's hotspot-tour / expanded-image chrome. Surfaces on vehicles
// without a loadable 3D camera model (no projected-hotspot layer → the MFE falls back to its
// PDP tour UI) — must never be visible or interactive in damage-marking mode. Mirrors the CSS
// rules in spinner-location.module.css; this JS pass is the fallback that also fires the
// drift telemetry when the tour actually appears.
// Every `tour-*` node is matched by prefix (the tour has 12+ data-qa hooks and the set varies
// by MFE version — enumerating them loses the race against the unpinned MFE); the
// expanded-image / hotspot-nav hooks don't share the prefix, so they're listed explicitly.
const TOUR_CHROME_SELECTOR = [
  "[data-qa^='tour-']",
  "[data-qa='expanded-image-element']",
  "[data-qa='media-content-wrapper']",
  "[data-qa='hotspot-previous-button']",
  "[data-qa='hotspot-next-button']",
  "[data-qa='hotspot-photo-expand-button']",
  "[data-qa='hotspot-vido-expand-button']",
  "[data-qa='hotspot-counter']",
].join(', ');

export function hideSpinnerChrome(root: Element): void {
  // Hotspot tour / expanded-image view (see TOUR_CHROME_SELECTOR). Report once per session so
  // we can see from RUM which vehicles drive customers into the tour path at all.
  root.querySelectorAll<HTMLElement>(TOUR_CHROME_SELECTOR).forEach((node) => {
    if (node.style.display !== 'none') {
      node.style.display = 'none';
      node.style.pointerEvents = 'none';
      reportShapeMiss('tour-ui-suppressed');
    }
  });

  // The MFE's native zoom-in/out buttons (SERV-580) — we drive zoom via our own slider, so
  // hide the MFE's. Prefer the button's wrapper unless it contains the car canvas.
  root.querySelectorAll<HTMLElement>('[data-test="zoom-in-button"], [data-test="zoom-out-button"]').forEach((btn) => {
    const wrapper = btn.parentElement as HTMLElement | null;
    const victim = wrapper && wrapper !== root && !wrapper.querySelector('canvas') ? wrapper : btn;
    if (victim.style.display !== 'none') {
      victim.style.display = 'none';
      victim.style.pointerEvents = 'none';
    }
  });

  // Immersive-exit button: hide the smallest button-sized chrome ancestor of the icon.
  root.querySelectorAll<SVGElement>('svg[data-qa="exit-cross-icon"]').forEach((icon) => {
    let target: HTMLElement | null = icon.parentElement;
    let chrome: HTMLElement | null = null;
    while (target && target !== root) {
      const r = target.getBoundingClientRect();
      if (r.width > MAX_CHROME_PX || r.height > MAX_CHROME_PX) break;
      chrome = target;
      target = target.parentElement;
    }
    const victim = chrome ?? (icon.parentElement as HTMLElement | null);
    if (victim && victim.style.display !== 'none') {
      victim.style.display = 'none';
      victim.style.pointerEvents = 'none';
    }
  });

  root.querySelectorAll<HTMLElement>('*').forEach((node) => {
    // Never touch our own overlay controls (e.g. the zoom slider is an <input type="range">
    // that would otherwise match the scrubber rule below).
    if (node.closest('[data-zoom-ui]')) return;
    const text = (node.textContent || '').trim().toLowerCase();
    const aria = (node.getAttribute('aria-label') || '').trim().toLowerCase();
    const role = (node.getAttribute('role') || '').toLowerCase();

    // Exterior/Interior(/Glass) view toggle → hide the buttons' wrapper.
    if (node.tagName === 'BUTTON' && (text === 'exterior' || text === 'interior') && node.parentElement) {
      node.parentElement.style.display = 'none';
    }

    // "rotate to view" drag hint / rotation scrubber.
    const isRotateHint =
      aria.includes('rotate') || aria.includes('swipe') || (text === 'rotate to view' && node.children.length === 0);
    const isScrubber = role === 'slider' || (node.tagName === 'INPUT' && node.getAttribute('type') === 'range');
    if (isRotateHint || isScrubber) {
      node.style.display = 'none';
      if (node.parentElement) node.parentElement.style.display = 'none';
    }

    // The "360°" drag-hint badge: match any element whose entire text is "360°" (text may be
    // split across spans) or whose aria-label mentions 360, then climb to the outermost
    // wrapper that still contains only that text and hide it (chevrons + circle go with it).
    if ((text === '360°' || aria.includes('360')) && !node.querySelector('canvas')) {
      let badge: HTMLElement = node;
      let parent = node.parentElement;
      while (parent && !parent.querySelector('canvas') && (parent.textContent || '').trim() === '360°') {
        badge = parent;
        parent = parent.parentElement;
      }
      badge.style.display = 'none';
    }
  });
}

// Chrome elements never exceed this box; used to bound the exit-button ancestor walk so a
// structural change can't make us hide the whole pane.
const MAX_CHROME_PX = 56;
